import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    FaCheckCircle, FaMapMarkerAlt, FaLink, FaHeartbeat, FaCalendarCheck,
    FaRegCalendarAlt, FaUserMd, FaEnvelope, FaSearch, FaBell,
    FaCommentAlt, FaFileMedical, FaClock, FaStethoscope, FaHistory, FaEdit,
    FaQuoteRight, FaUsers
} from 'react-icons/fa';
import './ProfilePatient.css';

// ── Helpers ─────────────────────────────────────────────────────────────────
const Avatar = ({ src, size = 160 }) => (
    <div className="pp-avatar-container" style={{ width: size, height: size }}>
        <div className="pp-avatar-glow"></div>
        <div className="pp-avatar-inner">
            <img src={src || '/patient_avatar.png'} alt="Patient" />
        </div>
    </div>
);

// ── Timeline Item ───────────────────────────────────────────────────────────
const TimelineItem = ({ item }) => (
    <div className="pp-timeline-item">
        <div className="pp-timeline-marker">
            <div className="pp-marker-dot"></div>
            <div className="pp-marker-line"></div>
        </div>
        <div className="pp-timeline-content">
            <div className="pp-timeline-header">
                <span className="pp-timeline-type">{item.type}</span>
                <span className="pp-timeline-date">{item.date}</span>
            </div>
            <h3 className="pp-timeline-title">{item.title}</h3>
            <p className="pp-timeline-desc">{item.description}</p>
            {item.doctor && <p className="pp-timeline-doctor"><FaUserMd /> {item.doctor}</p>}
        </div>
    </div>
);

// ── Mock Data ────────────────────────────────────────────────────────────────
const MOCK_PATIENT = {
    name: 'Sra. Martha Jiménez',
    badge: 'PACIENTE ACTIVO',
    condition: 'Paciente en EcoScienceInnova',
    location: 'Madrid, España',
    age: '54 años',
    description: 'Me interesa aprender sobre nutrición saludable y ejercicios de bajo impacto para mejorar mi bienestar diario.',
    interests: ['Nutrición', 'Yoga', 'Meditación', 'Salud Mental', 'Bienestar', 'Ejercicio Suave'],
    stats: [],
    history: [],
    team: [
        { name: 'Dr. Julián Castro', role: 'Oncólogo Principal', online: true },
        { name: 'Dra. Sofía Rivas', role: 'Especialista en Nutrición', online: false },
        { name: 'Lic. Roberto Cano', role: 'Coordinador de Estudio', online: true }
    ],
    appointments: [
        { day: '15', month: 'OCT', time: '10:30 AM', title: 'Revisión Mensual', place: 'Ala Norte - Consultorio 302' },
        { day: '02', month: 'NOV', time: '09:00 AM', title: 'Toma de Muestras', place: 'Laboratorio Nivel 1' }
    ]
};

const ProfilePatient = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('Intereses');

    const avatarPath = '/patient_avatar.png';

    return (
        <div className="pp-profile-page">
            {/* ── HEADER ── */}
            <header className="pp-header">
                <div className="pp-header-inner">
                    <div className="pp-logo" onClick={() => navigate('/community-feed')}>
                        <img src="/logoecseia.png" alt="Logo" style={{ width: '32px' }} />
                        <span><span style={{ color: '#FF6600' }}>Eco</span>ScienceInnova</span>
                    </div>
                    <div className="pp-search-container">
                        <FaSearch className="pp-search-icon" />
                        <input type="text" placeholder="Buscar citas, médicos, resultados..." />
                    </div>
                    <nav className="pp-nav-links">
                        <button className="active">Mi Salud</button>
                        <button>Mensajes</button>
                        <button onClick={() => navigate('/studies', { state: { from: 'internal' } })}>Estudios</button>
                        <button>Recursos</button>
                    </nav>
                    <div className="pp-header-actions">
                        <button className="pp-icon-btn"><FaBell /><span className="pp-dot" /></button>
                        <button className="pp-icon-btn"><FaCommentAlt /></button>
                        <div className="pp-mini-avatar" onClick={() => navigate('/profile-patient', { state: { from: 'internal' } })}>
                            <img src={avatarPath} alt="User" />
                        </div>
                    </div>
                </div>
            </header>

            <main className="pp-container">
                {/* ── PROFILE TOP SECTION ── */}
                <section className="pp-profile-top">
                    <Avatar src={avatarPath} />
                    <div className="pp-profile-info">
                        <div className="pp-name-row">
                            <h1>{MOCK_PATIENT.name}</h1>
                            <span className="pp-badge"><FaCheckCircle /> {MOCK_PATIENT.badge}</span>
                            <button className="pp-btn-edit-inline" onClick={() => navigate('/profile-patient/edit', { state: { from: 'internal' } })}>
                                <FaEdit /> Editar Perfil
                            </button>
                        </div>
                        <p className="pp-condition"><FaHeartbeat /> {MOCK_PATIENT.condition}</p>
                        <div className="pp-meta-row">
                            <span><FaMapMarkerAlt /> {MOCK_PATIENT.location}</span>
                            <span><FaClock /> {MOCK_PATIENT.age}</span>
                            <span><FaLink /> ID Paciente: #PC-7742</span>
                        </div>
                    </div>
                    <div className="pp-profile-actions">
                        <button className="pp-btn-emergency">Botón de Alerta</button>
                        <button className="pp-btn-message">Mensaje Urgente</button>
                    </div>
                </section>

                {/* ── BIO SECTION ── */}
                <section className="pp-bio-section">
                    <FaQuoteRight className="pp-quote-icon" />
                    <p className="pp-description">{MOCK_PATIENT.description}</p>
                </section>

                {/* ── CONTENT AREA ── */}
                <div className="pp-content-layout">
                    {/* LEFT CONTENT: TABS & INTERESTS */}
                    <div className="pp-left-content">
                        <nav className="pp-tabs">
                            {['Mi Comunidad', 'Intereses', 'Mensajes', 'Recursos'].map(tab => (
                                <button
                                    key={tab}
                                    className={activeTab === tab ? 'active' : ''}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </nav>

                        <div className="pp-tab-content">
                            {activeTab === 'Intereses' && (
                                <div className="pp-interests-view">
                                    <h3>Temas que sigo</h3>
                                    <div className="pp-interests-grid">
                                        {MOCK_PATIENT.interests.map((tag, i) => (
                                            <div key={i} className="pp-interest-pill">
                                                <FaCheckCircle /> {tag}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {activeTab === 'Mi Comunidad' && (
                                <div className="pp-community-placeholder">
                                    <FaUsers size={48} />
                                    <p>Próximamente: Conecta con pacientes con intereses similares.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT SIDEBAR */}
                    <aside className="pp-sidebar">
                        <div className="pp-sidebar-card pp-welcome-card">
                            <h3><FaUsers /> Mi Red</h3>
                            <p className="pp-sidebar-text">Conecta con otros pacientes que comparten tus intereses en Nutrición y Bienestar.</p>
                            <button className="pp-btn-connect">Explorar Comunidad</button>
                        </div>

                        <div className="pp-sidebar-card pp-promo-card">
                            <div className="pp-promo-content">
                                <span className="pp-promo-tag">RECURSO RECOMENDADO</span>
                                <h4>Guía de Meditación para Principiantes</h4>
                                <p>Aprende las bases de la relajación diaria.</p>
                            </div>
                        </div>
                    </aside>
                </div>
            </main>

            {/* ── FOOTER ── */}
            <footer className="pp-footer">
                <div className="pp-footer-inner">
                    <img src="/logoecseia.png" alt="Logo" style={{ width: '20px' }} />
                    <span><span style={{ color: '#FF6600' }}>Eco</span>ScienceInnova</span>
                    <div className="pp-footer-links">
                        <button>Privacidad de Datos Médicos</button>
                        <button>Ayuda al Paciente</button>
                        <button>Contacto Emergencias</button>
                    </div>
                    <p className="pp-copyright">© 2024 Eco ScienceInnova. Tu salud, nuestra prioridad.</p>
                </div>
            </footer>
        </div>
    );
};

export default ProfilePatient;
